<div class="container-fluid py-5">
  <div class="row justify-content-center">
    <div class="col-lg-8">
      <div class="card shadow-sm">
        <div class="card-header bg-primary text-white">
          <h2 class="mb-0">
            <span *ngIf="showConfirmation">Proof of Delivery - Confirmation</span>
            <span *ngIf="!showConfirmation && !showReview && confirmationType === 'collection'">Upload Collection Documents</span>
            <span *ngIf="!showConfirmation && !showReview && confirmationType === 'delivery'">Upload Delivery Documents</span>
            <span *ngIf="!showConfirmation && showReview && confirmationType === 'collection'">Review Collection Documents</span>
            <span *ngIf="!showConfirmation && showReview && confirmationType === 'delivery'">Review Delivery Documents</span>
          </h2>
          <div *ngIf="tokenValid && bookingInfo.booking_id" class="mt-2">
            <small>Booking ID: {{ bookingInfo.booking_id }}</small>
            <span *ngIf="bookingInfo.email" class="ms-3">
              <small>Email: {{ bookingInfo.email }}</small>
            </span>
          </div>
        </div>

        <div class="card-body">

          <!-- Verification Loading -->
          <div *ngIf="isVerifying" class="text-center py-5">
            <div class="spinner-border text-primary mb-3" role="status"></div>
            <h4>{{ statusMessage }}</h4>
          </div>

          <!-- Token Invalid Error -->
          <div *ngIf="!isVerifying && !tokenValid" class="alert alert-danger text-center">
            <i class="bi bi-exclamation-triangle fs-1 mb-3"></i>
            <h4>Upload Link Invalid</h4>
            <p class="mb-0">{{ statusMessage }}</p>
          </div>

          <!-- Confirmation Screen -->
          <div *ngIf="!isVerifying && tokenValid && showConfirmation" class="text-center py-5">
            <div *ngIf="!canUpload" class="alert alert-warning text-start mb-4">
              <i class="bi bi-eye me-2"></i>
              This upload link has expired. Existing documents can still be viewed, but new files cannot be uploaded.
            </div>
            <i class="bi bi-clipboard-check fs-1 text-primary mb-4"></i>
            <h3 class="mb-4">{{ canUpload ? 'What would you like to confirm?' : 'What would you like to view?' }}</h3>
            <p class="text-muted mb-4">
              {{ canUpload ? 'Please select the type of proof you want to upload:' : 'Please select a document type to view its existing proof:' }}
            </p>

            <div class="row justify-content-center">
              <div class="col-md-4 mb-3">
                <button class="btn btn-outline-success btn-lg w-100 py-3" (click)="confirmCollection()">
                  <i class="bi bi-box-seam fs-2 d-block mb-2"></i>
                  <strong>Confirm Collection</strong>
                  <small class="d-block text-muted">{{ canUpload ? 'Upload pickup documents' : 'View pickup document' }}</small>
                  <span *ngIf="bookingInfo.collection_uploaded" class="badge text-bg-success mt-2">Existing POD available</span>
                </button>
              </div>
              <div class="col-md-4 mb-3">
                <button class="btn btn-outline-primary btn-lg w-100 py-3" (click)="confirmDelivery()">
                  <i class="bi bi-truck fs-2 d-block mb-2"></i>
                  <strong>Confirm Delivery</strong>
                  <small class="d-block text-muted">{{ canUpload ? 'Upload delivery documents' : 'View delivery document' }}</small>
                  <span *ngIf="bookingInfo.delivery_uploaded" class="badge text-bg-success mt-2">Existing POD available</span>
                </button>
              </div>
            </div>
          </div>

          <!-- Existing Document Loading -->
          <div *ngIf="!isVerifying && tokenValid && isLoadingExistingDocument" class="text-center py-5">
            <div class="spinner-border text-primary mb-3" role="status"></div>
            <h4>Checking for an existing {{ confirmationType }} document...</h4>
          </div>

          <!-- Review Documents Section -->
          <div *ngIf="!isVerifying && tokenValid && !isLoadingExistingDocument && !showConfirmation && showReview">
            <!-- Back Button -->
            <div class="mb-3">
              <button class="btn btn-outline-secondary" (click)="goBackFromReview()" [disabled]="isUploading">
                <i class="bi bi-arrow-left me-2"></i>Back to Selection
              </button>
            </div>

            <div *ngIf="uploadStatus === 'success' && statusMessage" class="alert alert-success">
              <i class="bi bi-check-circle me-2"></i>{{ statusMessage }}
            </div>

            <!-- Document Preview Area -->
            <div class="row">
              <!-- Preview Panel -->
              <div class="col-md-8">
                <div class="preview-container bg-light rounded-3 p-4 text-center" style="min-height: 400px;">
                  <div *ngIf="reviewFiles.length > 0" class="h-100 d-flex flex-column">

                    <!-- Navigation Header -->
                    <div class="d-flex align-items-center mb-3">
                      <div class="flex-grow-1 me-3 min-w-0">
                        <h5 class="mb-0 text-truncate" [title]="reviewFiles[selectedPreviewIndex].name">
                          {{ reviewFiles[selectedPreviewIndex].name }}
                        </h5>
                      </div>
                      <div class="preview-nav flex-shrink-0">
                        <button
                          class="btn btn-outline-secondary btn-sm me-1"
                          [disabled]="selectedPreviewIndex === 0"
                          (click)="previousPreview()"
                        >
                          <i class="bi bi-chevron-left"></i>
                        </button>
                        <span class="mx-2 text-nowrap">{{ selectedPreviewIndex + 1 }} of {{ reviewFiles.length }}</span>
                        <button
                          class="btn btn-outline-secondary btn-sm ms-1"
                          [disabled]="selectedPreviewIndex === reviewFiles.length - 1"
                          (click)="nextPreview()"
                        >
                          <i class="bi bi-chevron-right"></i>
                        </button>
                      </div>
                    </div>

                    <!-- Preview Content -->
                    <div class="flex-grow-1 d-flex align-items-center justify-content-center">
                      <!-- Image Preview -->
                      <div *ngIf="isImageFile(reviewFiles[selectedPreviewIndex])" class="w-100">
                        <img
                          [src]="getFilePreviewUrl(reviewFiles[selectedPreviewIndex])"
                          [alt]="reviewFiles[selectedPreviewIndex].name"
                          class="img-fluid rounded shadow-sm"
                          style="max-height: 350px; max-width: 100%;"
                        >
                      </div>

                      <!-- PDF Preview -->
                      <div *ngIf="isPdfFile(reviewFiles[selectedPreviewIndex])" class="w-100">
                        <ng2-pdfjs-viewer
                          [pdfSrc]="getFilePreviewUrl(reviewFiles[selectedPreviewIndex])"
                          class="pdf-preview d-block border rounded shadow-sm bg-white"
                        ></ng2-pdfjs-viewer>
                        <a
                          [href]="getFilePreviewUrl(reviewFiles[selectedPreviewIndex])"
                          target="_blank"
                          class="btn btn-outline-primary btn-sm mt-3"
                        >
                          <i class="bi bi-box-arrow-up-right me-1"></i>Open PDF
                        </a>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <!-- Thumbnails Panel -->
              <div class="col-md-4">
                <h6 class="mb-3">Documents ({{ reviewFiles.length }})</h6>
                <div class="thumbnails-container">
                  <div
                    *ngFor="let file of reviewFiles; let i = index"
                    class="thumbnail-item mb-2 p-2 border rounded cursor-pointer"
                    [ngClass]="{ 'border-primary bg-light': i === selectedPreviewIndex }"
                    (click)="selectPreview(i)"
                    style="cursor: pointer;"
                  >
                    <div class="d-flex align-items-center">
                      <!-- Thumbnail -->
                      <div class="thumbnail-preview me-2 flex-shrink-0" style="width: 40px; height: 40px;">
                        <img
                          *ngIf="isImageFile(file)"
                          [src]="getFilePreviewUrl(file)"
                          [alt]="file.name"
                          class="img-fluid rounded"
                          style="width: 40px; height: 40px; object-fit: cover;"
                        >
                        <div
                          *ngIf="isPdfFile(file)"
                          class="d-flex align-items-center justify-content-center bg-danger text-white rounded"
                          style="width: 40px; height: 40px;"
                        >
                          <i class="bi bi-file-earmark-pdf" style="font-size: 0.9rem;"></i>
                        </div>
                      </div>

                      <!-- File Info -->
                      <div class="flex-grow-1 min-w-0 overflow-hidden">
                        <div class="fw-semibold text-truncate" style="font-size: 0.75rem; line-height: 1.2;" [title]="file.name">
                          {{ file.name }}
                        </div>
                        <div class="text-muted" style="font-size: 0.7rem;">{{ getFileSizeString(file.size) }}</div>
                        <span *ngIf="isExistingReviewFile(i)" class="badge text-bg-secondary mt-1">Existing document</span>
                      </div>

                      <!-- Remove Button -->
                      <button
                        *ngIf="canUpload && !isExistingReviewFile(i)"
                        class="btn btn-sm btn-outline-danger flex-shrink-0 ms-1 p-1"
                        (click)="removeReviewFile(i); $event.stopPropagation()"
                        title="Remove file"
                        style="width: 28px; height: 28px; font-size: 0.7rem;"
                      >
                        <i class="bi bi-trash"></i>
                      </button>
                    </div>
                  </div>
                </div>

                <!-- Action Buttons -->
                <div *ngIf="canUpload" class="mt-4">
                  <label class="btn btn-outline-primary w-100 mb-2">
                    <i class="bi bi-plus-circle me-2"></i>Add More Files
                    <input
                      type="file"
                      multiple
                      class="d-none"
                      accept=".pdf,.jpeg,.jpg,.png"
                      aria-label="Add more files"
                      (change)="onFileSelected($event)"
                    >
                  </label>
                  <button
                    class="btn btn-success w-100"
                    (click)="uploadFiles()"
                    [disabled]="isUploading || files.length === 0"
                  >
                    <span *ngIf="isUploading" class="spinner-border spinner-border-sm me-2"></span>
                    <i *ngIf="!isUploading" class="bi bi-upload me-2"></i>
                    {{ isUploading ? 'Uploading...' : 'Confirm & Upload' }}
                  </button>
                </div>
                <div *ngIf="!canUpload" class="alert alert-info mt-4 mb-0">
                  <i class="bi bi-lock me-2"></i>Read-only document
                </div>
              </div>
            </div>
          </div>

          <!-- Upload Area (only show when token is valid and confirmation is done) -->
          <div *ngIf="!isVerifying && tokenValid && !isLoadingExistingDocument && !showConfirmation && !showReview">

            <!-- Back Button -->
            <div class="mb-3">
              <button class="btn btn-outline-secondary" (click)="goBackToConfirmation()">
                <i class="bi bi-arrow-left me-2"></i>Back to Selection
              </button>
            </div>
            <div *ngIf="!canUpload" class="alert alert-warning text-center py-4">
              <i class="bi bi-file-earmark-x fs-2 d-block mb-2"></i>
              <strong>{{ statusMessage || 'No existing document is available for this type.' }}</strong>
              <div class="mt-1">This link has expired and cannot accept new uploads.</div>
            </div>
            <div
              *ngIf="canUpload && !existingDocumentUnavailable"
              class="upload-area border-2 border-dashed rounded-4 p-5 text-center mb-4"
              [ngClass]="{
                'border-primary bg-light': isDragOver,
                'border-secondary': !isDragOver && uploadStatus !== 'error',
                'border-danger bg-danger-subtle': uploadStatus === 'error',
                'border-success bg-success-subtle': uploadStatus === 'success'
              }"
              (dragover)="onDragOver($event)"
              (dragleave)="onDragLeave($event)"
              (drop)="onDrop($event)"
            >
              <div>
                <i class="bi bi-cloud-upload fs-1 text-muted mb-3"></i>
                <h4 class="mb-3">Drag and drop files here</h4>
                <p class="text-muted mb-3">or</p>
                <label class="btn btn-outline-primary btn-lg">
                  <i class="bi bi-folder-plus me-2"></i>Browse Files
                  <input
                    type="file"
                    multiple
                    class="d-none"
                    accept=".pdf,.jpeg,.jpg,.png"
                    aria-label="Browse files"
                    (change)="onFileSelected($event)"
                  >
                </label>
                <div class="mt-3">
                  <small class="text-muted">
                    Supported formats: JPG, PNG, PDF (Max 10MB per file)
                  </small>
                </div>
              </div>

            </div>

            <!-- Selected Files -->
            <div *ngIf="canUpload && !existingDocumentUnavailable && files.length > 0" class="selected-files mb-4">
              <h5 class="mb-3">Selected Files ({{ files.length }})</h5>
              <div class="row g-2">
                <div *ngFor="let file of files; let i = index" class="col-md-6">
                  <div class="card">
                    <div class="card-body p-3">
                      <div class="d-flex align-items-center justify-content-between">
                        <div class="d-flex align-items-center flex-grow-1">
                          <i [class]="getFileIcon(file)" class="fs-4 text-primary me-2"></i>
                          <div class="file-info flex-grow-1">
                            <div class="fw-semibold text-truncate" [title]="file.name">{{ file.name }}</div>
                            <small class="text-muted">{{ getFileSizeString(file.size) }}</small>
                          </div>
                        </div>
                        <button
                          class="btn btn-sm btn-outline-danger"
                          (click)="removeFile(i)"
                          [disabled]="isUploading"
                        >
                          <i class="bi bi-trash"></i>
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- Status Messages -->
            <div *ngIf="canUpload && statusMessage && uploadStatus !== 'success'" class="alert" [ngClass]="{
              'alert-info': uploadStatus === 'uploading',
              'alert-danger': uploadStatus === 'error'
            }">
              <div *ngIf="uploadStatus === 'uploading'" class="d-flex align-items-center">
                <div class="spinner-border spinner-border-sm me-2" role="status"></div>
                {{ statusMessage }}
              </div>
              <div *ngIf="uploadStatus === 'error'">
                <i class="bi bi-exclamation-circle me-2"></i>
                {{ statusMessage }}
              </div>
            </div>

            <!-- Validation Errors -->
            <div *ngIf="hasValidationErrors()" class="alert alert-danger">
              <h6><i class="bi bi-exclamation-triangle me-2"></i>Validation Errors:</h6>
              <ul class="mb-0">
                <li *ngFor="let error of getValidationErrorsList()">{{ error }}</li>
              </ul>
            </div>

            <!-- Review Button -->
            <div *ngIf="canUpload && !existingDocumentUnavailable && files.length > 0" class="text-center">
              <button
                class="btn btn-primary btn-lg"
                (click)="proceedToReview()"
                [disabled]="files.length === 0"
              >
                <i class="bi bi-eye me-2"></i>Review Documents
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</div>
